import { isSupabaseStorageUrl } from './video/player';

// Ad creatives reach this code in two shapes: raw snake_case rows straight
// from PostgREST, and the camelCased objects the store produces. Read both so
// callers never have to care which one they are holding.
const pick = (c: any, ...keys: string[]): any => {
  for (const key of keys) {
    const value = c?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

/**
 * The TikTok Spark Ads auth code for a creative, or null.
 *
 * Natively submitted TikTok creatives keep the code in `content` (it always
 * starts with '#'); creatives merged from another row carry it in
 * merged_tiktok_auth_code. Only checking the latter is what made the bulk-add
 * button report 0 eligible creatives when 28 were in fact usable.
 */
export function getTikTokAuthCode(creative: any): string | null {
  const merged = pick(creative, 'mergedTiktokAuthCode', 'merged_tiktok_auth_code');
  if (merged) return String(merged);
  const content = pick(creative, 'content');
  if (typeof content === 'string' && content.startsWith('#')) return content;
  return null;
}

/** Set once a creative is live as a TikTok ad — our de-duplication key. */
export const getTikTokAdId = (creative: any): string | null =>
  pick(creative, 'tiktok_ad_id', 'tiktokAdId');

const isAlreadyHandled = (creative: any): boolean => {
  const status = pick(creative, 'status');
  return status === 'active' || status === 'error';
};

/**
 * Eligible for TikTok = we have an auth code, the creative still needs
 * handling, and it is not already an ad on TikTok.
 *
 * `tiktok_ad_id` is only trustworthy after reconcileTikTokCampaign() has
 * backfilled it from the live campaign — call that first for a bulk add,
 * otherwise creatives already on TikTok look eligible and get duplicated.
 */
export function isTikTokEligible(creative: any): boolean {
  if (isAlreadyHandled(creative)) return false;
  if (getTikTokAdId(creative)) return false;
  return !!getTikTokAuthCode(creative);
}

/** Eligible for Meta = there is an actual video we can upload. */
export function isMetaEligible(creative: any): boolean {
  if (isAlreadyHandled(creative)) return false;
  const content = (pick(creative, 'content') as string | null) || '';
  const platform = pick(creative, 'platform');
  const reelUrl = pick(creative, 'mergedInstagramReelUrl', 'merged_instagram_reel_url');
  const hasInstagram = platform === 'instagram' && (!!reelUrl || content.includes('instagram.com/'));
  const isDropbox = content.includes('dropbox.com');
  return isSupabaseStorageUrl(content) || isDropbox || hasInstagram;
}
